import { useRef, useState, useEffect } from "react";
import classNames from "classnames";
import styles from "./hotspots.module.css";

const Hotspots = ({
  large,
  hotspots,
  onChange = () => {},
  hotspotSizePx = 32,
}) => {
  const containerRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [dragOffsetPct, setDragOffsetPct] = useState({ dx: 0, dy: 0 });

  const getContainerSize = () => {
    const el = containerRef.current;
    return el
      ? { w: el.clientWidth || 1, h: el.clientHeight || 1 }
      : { w: 1, h: 1 };
  };

  const pxToPct = (x, y) => {
    const { w, h } = getContainerSize();
    return { x: (x / w) * 100, y: (y / h) * 100 };
  };

  const pointerToContainerPx = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  const beginDrag = (e, id) => {
    e.preventDefault();
    e.stopPropagation();

    const hs = hotspots.find((h) => h.id === id);
    if (!hs) return;
    const { x: px, y: py } = pointerToContainerPx(e);
    const { w, h } = getContainerSize();

    // offset from the hotspot center to the pointer (in PERCENT)
    const dxPct = ((px - (hs.x / 100) * w) / w) * 100;
    const dyPct = ((py - (hs.y / 100) * h) / h) * 100;

    setDragId(id);
    setDragOffsetPct({ dx: dxPct, dy: dyPct });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (dragId == null) return;
    const { x: px, y: py } = pointerToContainerPx(e);
    const { x: pX, y: pY } = pxToPct(px, py);

    const rX = (hotspotSizePx / 2 / getContainerSize().w) * 100;
    const rY = (hotspotSizePx / 2 / getContainerSize().h) * 100;

    const next = hotspots.map((h) =>
      h.id === dragId
        ? {
            ...h,
            x: clamp(pX - dragOffsetPct.dx, 0 + rX, 100 - rX),
            y: clamp(pY - dragOffsetPct.dy, 0 + rY, 100 - rY),
          }
        : h
    );

    onChange?.(next);
  };

  const endDrag = (e) => {
    if (dragId == null) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setDragId(null);
  };

  useEffect(() => {
    const onWinResize = () => setDragId(null);
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, []);

  return (
    <div
      ref={containerRef}
      className={styles.hotspotContainer}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {hotspots?.map((hotspot) => (
        <div
          key={hotspot.id}
          className={classNames(styles.hotspot, {
            [styles.large]: large,
            [styles.dragging]: dragId === hotspot.id,
          })}
          style={{ top: `${hotspot?.y}%`, left: `${hotspot?.x}%` }}
          onPointerDown={(e) => beginDrag(e, hotspot.id)}
          role="button"
          aria-label={hotspot.hotspotLabel || "Hotspot"}
          title="Drag to move"
        >
          {large ? hotspot?.hotspotLabel : null}
        </div>
      ))}
    </div>
  );
};

export default Hotspots;
